import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { StatusStamp } from "../components/StatusStamp";
import { useAuth } from "../auth/AuthContext";
import { fetchContracts, type Contract } from "../lib/contracts";
import { fetchCounterparties, type Counterparty } from "../lib/counterparties";
import { formatDate, formatMoney } from "../lib/format";
import {
  DEBTOR_LABELS,
  OBLIGATION_STATUS_META,
  OBLIGATION_TYPE_LABELS,
  type ObligationStatus,
  type ObligationType,
} from "../lib/obligations";
import { ROLES, ROLE_LABELS, type Role } from "../lib/roles";
import {
  fetchObligationRegistry,
  obligationHref,
  type RegistryFilters,
  type SummaryRow,
} from "../lib/summary";

/**
 * Реестр обязательств (E12, ТЗ 4.2.6): «фильтры: статус, тип, сторона-должник, ответственный,
 * контрагент, договор; сортировка по критичности».
 *
 * Виден ВСЕМ шести ролям — строка 4.3 «Просмотр реестров договоров и обязательств» (в отличие от
 * сводки, куда делопроизводитель не допущен). Поэтому здесь нет начисленных санкций: они
 * содержание сводки. Сумма и остаток обязательства есть — они видны и в карточке договора.
 *
 * Порядок строк задаёт СЕРВЕР (по критичности), клиент его не пересортировывает: иначе реестр и
 * сводка выстраивали бы одни и те же просрочки по-разному.
 *
 * Фильтры живут в адресе страницы: так переход «клик в сводке → отфильтрованный реестр» работает
 * обычной ссылкой, а сам отбор можно переслать коллеге.
 */

/** Размер страницы. Совпадает с умолчанием сервера (`DEFAULT_REGISTRY_PAGE_SIZE`, api). */
const PAGE_SIZE = 50;

const inputCls =
  "w-full rounded-[var(--radius-m)] border border-border bg-surface px-2.5 py-1.5 text-[14px]";
const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface disabled:opacity-50";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg disabled:opacity-50";
const cellCls = "px-2 py-1.5 align-top";
const headCls = "px-2 py-1.5 text-left text-[12px] font-bold text-muted";

/** Ключи фильтров = имена параметров адреса и полей запроса — один словарь на всё. */
const FILTER_KEYS = [
  "status",
  "type",
  "debtor",
  "responsible",
  "counterpartyId",
  "contractId",
] as const;
type FilterKey = (typeof FILTER_KEYS)[number];
type FilterDraft = Record<FilterKey, string>;

const EMPTY_DRAFT: FilterDraft = {
  status: "",
  type: "",
  debtor: "",
  responsible: "",
  counterpartyId: "",
  contractId: "",
};

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly rows: SummaryRow[]; readonly total: number }
  | { readonly kind: "error"; readonly message: string };

function draftFromParams(params: URLSearchParams): FilterDraft {
  const draft = { ...EMPTY_DRAFT };
  for (const key of FILTER_KEYS) draft[key] = params.get(key) ?? "";
  return draft;
}

export function ObligationsRegistryPage() {
  const { logout } = useAuth();
  const [params, setParams] = useSearchParams();

  // Применённый отбор — из адреса: переход по ссылке из сводки сразу открывает нужный срез.
  const applied = useMemo(() => draftFromParams(params), [params]);
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  // Черновик полей отделён от применённого: печатать можно свободно, запрос уходит по «Показать».
  const [draft, setDraft] = useState<FilterDraft>(applied);
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);

  // Адрес поменялся снаружи (ссылка из сводки, «назад» в браузере) — подхватываем в поля.
  useEffect(() => {
    setDraft(applied);
  }, [applied]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const res = await fetchObligationRegistry(applied as RegistryFilters, page, PAGE_SIZE);
    if (res.ok) {
      setState({ kind: "ready", rows: res.value.rows, total: res.value.total });
      return;
    }
    if (res.reason === "unauthorized") {
      await logout();
      return;
    }
    setState({ kind: "error", message: res.message });
  }, [applied, page, logout]);

  useEffect(() => {
    void load();
  }, [load]);

  // Справочники для выпадающих списков — по одному разу на открытие раздела.
  useEffect(() => {
    void fetchCounterparties().then((res) => {
      if (res.ok) setCounterparties(res.value);
    });
    void fetchContracts().then((res) => {
      if (res.ok) setContracts(res.contracts);
    });
  }, []);

  const applyFilter = (next: FilterDraft, nextPage = 1) => {
    const search = new URLSearchParams();
    for (const key of FILTER_KEYS) {
      if (next[key] !== "") search.set(key, next[key]);
    }
    if (nextPage > 1) search.set("page", String(nextPage));
    setParams(search);
  };

  const total = state.kind === "ready" ? state.total : 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section aria-labelledby="registry-title">
      <h1 id="registry-title" className="text-[20px] font-bold text-ink">
        Обязательства
      </h1>
      <p className="mt-1 text-[13px] text-muted">
        Обязательства по действующим договорам. Сначала — самое критичное: просрочки с эскалацией,
        затем остальные просрочки, споры и приближающиеся сроки.
      </p>

      <Filters
        draft={draft}
        counterparties={counterparties}
        contracts={contracts}
        onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
        onApply={() => applyFilter(draft)}
        onReset={() => applyFilter(EMPTY_DRAFT)}
      />

      <div className="mt-4">
        {state.kind === "loading" && (
          <p role="status" className="text-[13px] text-muted">
            Загрузка…
          </p>
        )}
        {state.kind === "error" && (
          <>
            <p role="alert" className="text-[13px] text-overdue-fg">
              {state.message}
            </p>
            <button type="button" className={`mt-3 ${btnSecondary}`} onClick={() => void load()}>
              Повторить
            </button>
          </>
        )}
        {state.kind === "ready" && <RegistryTable rows={state.rows} />}
      </div>

      {state.kind === "ready" && total > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          onPage={(p) => applyFilter(applied, p)}
        />
      )}
    </section>
  );
}

function Filters({
  draft,
  counterparties,
  contracts,
  onChange,
  onApply,
  onReset,
}: {
  draft: FilterDraft;
  counterparties: Counterparty[];
  contracts: Contract[];
  onChange: (patch: Partial<FilterDraft>) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <form
      aria-label="Фильтры реестра обязательств"
      className="mt-4 rounded-[var(--radius-l)] border border-border bg-surface p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <label htmlFor="rf-status" className="mb-1 block text-[13px] text-muted">
            Статус
          </label>
          <select
            id="rf-status"
            value={draft.status}
            onChange={(e) => onChange({ status: e.target.value })}
            className={inputCls}
          >
            <option value="">Любой</option>
            {(Object.keys(OBLIGATION_STATUS_META) as ObligationStatus[]).map((s) => (
              <option key={s} value={s}>
                {OBLIGATION_STATUS_META[s].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rf-type" className="mb-1 block text-[13px] text-muted">
            Тип обязательства
          </label>
          <select
            id="rf-type"
            value={draft.type}
            onChange={(e) => onChange({ type: e.target.value })}
            className={inputCls}
          >
            <option value="">Любой</option>
            {(Object.keys(OBLIGATION_TYPE_LABELS) as ObligationType[]).map((t) => (
              <option key={t} value={t}>
                {OBLIGATION_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rf-debtor" className="mb-1 block text-[13px] text-muted">
            Сторона-должник
          </label>
          <select
            id="rf-debtor"
            value={draft.debtor}
            onChange={(e) => onChange({ debtor: e.target.value })}
            className={inputCls}
          >
            <option value="">Любая</option>
            <option value="they">{DEBTOR_LABELS.they}</option>
            <option value="we">{DEBTOR_LABELS.we}</option>
          </select>
        </div>
        <div>
          <label htmlFor="rf-responsible" className="mb-1 block text-[13px] text-muted">
            Ответственный
          </label>
          <select
            id="rf-responsible"
            value={draft.responsible}
            onChange={(e) => onChange({ responsible: e.target.value })}
            className={inputCls}
          >
            <option value="">Любой</option>
            {ROLES.map((r: Role) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rf-counterparty" className="mb-1 block text-[13px] text-muted">
            Контрагент
          </label>
          <select
            id="rf-counterparty"
            value={draft.counterpartyId}
            onChange={(e) => onChange({ counterpartyId: e.target.value })}
            className={inputCls}
          >
            <option value="">Все контрагенты</option>
            {counterparties.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rf-contract" className="mb-1 block text-[13px] text-muted">
            Договор
          </label>
          <select
            id="rf-contract"
            value={draft.contractId}
            onChange={(e) => onChange({ contractId: e.target.value })}
            className={inputCls}
          >
            <option value="">Все договоры</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.number}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button type="submit" className={btnPrimary}>
          Показать
        </button>
        <button type="button" onClick={onReset} className={btnSecondary}>
          Сбросить
        </button>
      </div>
    </form>
  );
}

/** Таблица реестра: договор, контрагент, обязательство, ответственный, срок, статус. */
function RegistryTable({ rows }: { rows: readonly SummaryRow[] }) {
  if (rows.length === 0) {
    return <p className="text-[13px] text-muted">По заданным условиям обязательств нет.</p>;
  }
  return (
    <div>
      <table className="sticky-head sticky-head--on-bg w-full border-collapse text-[13px]">
        <caption className="sr-only">
          Реестр обязательств: договор, должник, контрагент, обязательство, тип, ответственный,
          срок, просрочка, остаток, статус. Порядок — по критичности.
        </caption>
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className={headCls}>
              Договор
            </th>
            <th scope="col" className={headCls}>
              Должник
            </th>
            <th scope="col" className={headCls}>
              Контрагент
            </th>
            <th scope="col" className={headCls}>
              Обязательство
            </th>
            <th scope="col" className={headCls}>
              Тип
            </th>
            <th scope="col" className={headCls}>
              Ответственный
            </th>
            <th scope="col" className={headCls}>
              Срок
            </th>
            <th scope="col" className={headCls}>
              Просрочка
            </th>
            <th scope="col" className={headCls}>
              Остаток
            </th>
            <th scope="col" className={headCls}>
              Статус
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.obligationId} className="border-b border-border-soft">
              <td className={`${cellCls} whitespace-nowrap`}>
                <Link className="text-accent hover:underline" to={obligationHref(row)}>
                  {row.contractNumber}
                </Link>
              </td>
              <td className={`${cellCls} whitespace-nowrap text-muted`}>
                {DEBTOR_LABELS[row.debtor]}
              </td>
              <td className={cellCls}>{row.counterpartyName}</td>
              <td className={cellCls}>{row.descr}</td>
              <td className={`${cellCls} whitespace-nowrap text-muted`}>{row.typeLabel}</td>
              <td className={`${cellCls} whitespace-nowrap`}>
                {row.responsible === null ? "—" : ROLE_LABELS[row.responsible]}
              </td>
              <td className={`${cellCls} num whitespace-nowrap`}>{formatDate(row.due)}</td>
              <td className={`${cellCls} num whitespace-nowrap`}>
                {row.overdue > 0 ? (
                  <span className="text-overdue-fg">{row.overdue} дн.</span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              <td className={`${cellCls} num whitespace-nowrap`}>
                {formatMoney(row.remaining ?? row.amount)}
              </td>
              <td className={`${cellCls} whitespace-nowrap`}>
                <StatusStamp status={row.status} escalated={row.escalated} short />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Постраничная навигация — как в журнале аудита: «назад/вперёд» и счётчик. */
function Pagination({
  page,
  totalPages,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  onPage: (p: number) => void;
}) {
  return (
    <div className="mt-3 flex items-center justify-between text-[13px] text-muted">
      <span>
        Всего обязательств: <span className="num text-ink">{total}</span>
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={btnSecondary}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Назад
        </button>
        <span className="num">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          className={btnSecondary}
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          Вперёд
        </button>
      </div>
    </div>
  );
}
